import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Snackbar,
  Checkbox,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { verifyIdentity, GetUser } from '../../services/profile';
import { PhotoCamera, Close as CloseIcon } from '@mui/icons-material';
import { LegalInline } from '../../components/Legal/LegalPage';

// Biyometrik açık rıza metninin sürümü — backend BiometricConsent ile birlikte kaydeder.
// Rıza metni (identityVerification.consent.label / legal.explicitConsent "biyometrik") değişirse artırın.
const KYC_CONSENT_VERSION = 'kyc-2026-10-21';

// Backend DocumentType: 0 IdentityCard (TC kimlik kartı), 1 Passport, 2 DriverLicense, 3 ResidencePermit, ...
const DOC_TYPE_IDENTITY_CARD = '0';
const DOC_TYPE_PASSPORT = '1';

// İstemci tarafı görsel kontrolleri (KYC servisi dosya başına en fazla 10 MB, JPEG/PNG kabul eder)
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MIN_LONG_EDGE = { document: 1000, selfie: 600 };
const MIME_EXT = { 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/pjpeg': 'jpg', 'image/png': 'png' };
const NAME_EXT = { jpg: 'jpg', jpeg: 'jpg', png: 'png' };

const FIELDS = {
  documentFrontImage: { preview: 'front', kind: 'document', fileName: 'document-front' },
  documentBackImage: { preview: 'back', kind: 'document', fileName: 'document-back' },
  selfieImage: { preview: 'selfie', kind: 'selfie', fileName: 'selfie' }
};

/** Görselin piksel boyutlarını okur. */
const readImageSize = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('unreadable'));
    };
    img.src = url;
  });

/**
 * Tür, boyut ve çözünürlük kontrolü. Sorun varsa { error: i18nAnahtarı }, yoksa
 * sunucunun uzantı kontrolünden geçecek şekilde yeniden adlandırılmış { file } döner.
 */
const checkImage = async (file, field) => {
  const { kind, fileName } = FIELDS[field];
  const nameExt = NAME_EXT[(file.name || '').split('.').pop().toLowerCase()];
  const ext = file.type ? MIME_EXT[file.type.toLowerCase()] : nameExt;
  if (!ext) return { error: 'identityVerification.imageErrors.type' };
  if (file.size > MAX_IMAGE_BYTES) return { error: 'identityVerification.imageErrors.size' };
  let size;
  try {
    size = await readImageSize(file);
  } catch (err) {
    return { error: 'identityVerification.imageErrors.unreadable' };
  }
  // Uzun kenar kullanılır; EXIF yönünden bağımsızdır
  if (Math.max(size.width, size.height) < MIN_LONG_EDGE[kind]) {
    return {
      error: kind === 'selfie'
        ? 'identityVerification.imageErrors.resolutionSelfie'
        : 'identityVerification.imageErrors.resolutionDocument'
    };
  }
  const type = ext === 'png' ? 'image/png' : 'image/jpeg';
  return { file: new File([file], `${fileName}.${ext}`, { type }) };
};

const TipsBox = ({ title, items }) => (
  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'action.hover' }}>
    <Typography variant="subtitle2" gutterBottom>
      {title}
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {items.map((item) => (
        <Typography component="li" variant="body2" key={item} sx={{ mb: 0.25 }}>
          {item}
        </Typography>
      ))}
    </Box>
  </Box>
);

const IdentityVerification = () => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const previewRef = useRef({ front: null, back: null, selfie: null });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    nationality: '',
    documentType: '',
    documentFrontImage: null,
    documentBackImage: null,
    selfieImage: null
  });

  const [preview, setPreview] = useState({
    front: null,
    back: null,
    selfie: null
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [error, setError] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [biometricConsent, setBiometricConsent] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const isIdentityCard = formData.documentType === DOC_TYPE_IDENTITY_CARD;

  // Kullanıcı bilgilerini ve doğrulama durumunu çek
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await GetUser();
        if (response.statusCode === 200 && response.data) {
          // Kullanıcı adı ve soyadını form verilerine ekle
          setFormData(prev => ({
            ...prev,
            firstName: response.data.name || '',
            lastName: response.data.lastName || ''
          }));

          // Doğrulama durumunu kontrol et
          if (response.data.identityVerification !== undefined) {
            setVerificationStatus(response.data.identityVerification);
          }
          // Ret notu (API sağlıyorsa): Rejected ekranında ve yeni başvuru formunun üstünde gösterilir
          if (response.data.identityRejectionReason) {
            setRejectionReason(response.data.identityRejectionReason);
          }
        }
      } catch (err) {
        console.error('Kullanıcı bilgileri çekilirken hata oluştu:', err);
        toast.error(t('common.error'));
      } finally {
        setInitialLoading(false);
      }
    };

    fetchUserData();
  }, [t]);

  // Güncel önizleme adresleri (yenisi seçildiğinde ve sayfadan çıkarken serbest bırakmak için)
  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  // Kamera açıldığında akışı video öğesine bağla (video yalnızca cameraActive iken çizilir)
  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraActive]);

  // Sayfadan çıkarken önizlemeleri ve kamerayı temizle
  useEffect(() => {
    const previews = previewRef;
    const stream = streamRef;
    return () => {
      Object.values(previews.current).forEach((url) => url && URL.revokeObjectURL(url));
      if (stream.current) {
        stream.current.getTracks().forEach((track) => track.stop());
        stream.current = null;
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (name === 'documentType' && value !== DOC_TYPE_IDENTITY_CARD) {
      setFieldErrors(prev => ({ ...prev, documentBackImage: null }));
    }
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      dateOfBirth: date
    });
  };

  const setImage = (field, file) => {
    const key = FIELDS[field].preview;
    const oldUrl = previewRef.current[key];
    if (oldUrl) URL.revokeObjectURL(oldUrl);
    const url = file ? URL.createObjectURL(file) : null;
    previewRef.current = { ...previewRef.current, [key]: url };
    setFormData(prev => ({ ...prev, [field]: file }));
    setPreview(prev => ({ ...prev, [key]: url }));
    setFieldErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleFileChange = async (e) => {
    const input = e.target;
    const { name } = input;
    const file = input.files && input.files[0];
    // Aynı dosya yeniden seçilebilsin
    input.value = '';
    if (!file || !FIELDS[name]) return;

    const result = await checkImage(file, name);
    if (result.error) {
      setFieldErrors(prev => ({ ...prev, [name]: t(result.error) }));
      return;
    }
    setImage(name, result.file);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    setCameraError(false);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError(true);
      return;
    }
    try {
      // Yüz karşılaştırması için mümkün olan en iyi çözünürlüğü iste
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      setCameraActive(true);
    } catch (err) {
      console.error('Kamera erişim hatası:', err);
      setCameraError(true);
      toast.error(t('identityVerification.cameraError'));
    }
  };

  const takeSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;

    try {
      const { videoWidth, videoHeight } = video;
      if (Math.max(videoWidth || 0, videoHeight || 0) < MIN_LONG_EDGE.selfie) {
        setFieldErrors(prev => ({ ...prev, selfieImage: t('identityVerification.imageErrors.resolutionSelfie') }));
        stopCamera();
        return;
      }
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(blob => {
        if (blob) {
          setImage('selfieImage', new File([blob], 'selfie.jpg', { type: 'image/jpeg' }));
        } else {
          toast.error(t('identityVerification.selfieError'));
        }
        stopCamera();
      }, 'image/jpeg', 0.92);
    } catch (err) {
      console.error('Selfie çekerken hata oluştu:', err);
      toast.error(t('identityVerification.selfieError'));
      stopCamera();
    }
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName || !formData.dateOfBirth ||
        !formData.documentType || !formData.documentFrontImage || !formData.selfieImage) {
      toast.error(t('identityVerification.fillAllFields'));
      return false;
    }
    // TC kimlik kartında MRZ arka yüzde: arka yüz zorunlu
    if (isIdentityCard && !formData.documentBackImage) {
      const message = t('identityVerification.imageErrors.backRequired');
      setFieldErrors(prev => ({ ...prev, documentBackImage: message }));
      toast.error(message);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('FirstName', formData.firstName);
      formDataToSend.append('LastName', formData.lastName);
      formDataToSend.append('DateOfBirth', formData.dateOfBirth.toISOString());
      formDataToSend.append('Nationality', formData.nationality);
      formDataToSend.append('DocumentType', formData.documentType);
      formDataToSend.append('DocumentFrontImage', formData.documentFrontImage);
      if (formData.documentBackImage) {
        formDataToSend.append('DocumentBackImage', formData.documentBackImage);
      }
      formDataToSend.append('SelfieImage', formData.selfieImage);
      // KVKK m.6 açık rıza: işaretlenmezse otomatik yüz karşılaştırması yapılmaz (manuel inceleme)
      formDataToSend.append('BiometricConsent', biometricConsent ? 'true' : 'false');
      formDataToSend.append('ConsentVersion', KYC_CONSENT_VERSION);

      const response = await verifyIdentity(formDataToSend);

      if (response.statusCode === 200) {
        toast.success(t('identityVerification.success'));
        setJustSubmitted(true);
        setVerificationStatus(0); // Pending
      } else {
        setError(response.message || t('identityVerification.error'));
        toast.error(response.message || t('identityVerification.error'));
      }
    } catch (err) {
      console.error('Kimlik doğrulama hatası:', err);
      setError(t('identityVerification.error'));
      toast.error(t('identityVerification.error'));
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Doğrulanmış hesap
  if (verificationStatus === 1) { // Approved
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Typography variant="h5" gutterBottom align="center" color="success.main">
          {t('identityVerification.approved.title')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'success.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 40
            }}
          >
            ✓
          </Box>
        </Box>
        <Typography paragraph align="center">
          {t('identityVerification.approved.message')}
        </Typography>
        <Alert severity="success" sx={{ mt: 2 }}>
          {t('identityVerification.approved.badge')}
        </Alert>
      </Paper>
    );
  }

  // Bekleyen doğrulama
  if (verificationStatus === 0 || verificationStatus === 3) { // Pending or UnderReview
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Typography variant="h5" gutterBottom align="center">
          {t('identityVerification.pending.title')}
        </Typography>
        {justSubmitted && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('identityVerification.submitted')}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={80} />
        </Box>
        <Typography paragraph align="center">
          {t('identityVerification.pending.message')}
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('identityVerification.pending.info')}
        </Alert>
      </Paper>
    );
  }

  // Reddedilmiş doğrulama
  if (verificationStatus === 2) { // Rejected
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Typography variant="h5" gutterBottom align="center" color="error">
          {t('identityVerification.rejected.title')}
        </Typography>
        <Typography paragraph align="center">
          {t('identityVerification.rejected.message')}
        </Typography>

        {rejectionReason && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('identityVerification.rejected.reason')}:
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {rejectionReason}
            </Alert>
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => setVerificationStatus(null)}
          sx={{ mt: 2 }}
        >
          {t('identityVerification.tryAgain')}
        </Button>
      </Paper>
    );
  }

  const documentTips = [
    t('identityVerification.tips.document1'),
    t('identityVerification.tips.document2'),
    t('identityVerification.tips.document3')
  ];
  if (formData.documentType === DOC_TYPE_PASSPORT) {
    documentTips.push(t('identityVerification.tips.document4Passport'));
  } else if (formData.documentType === '' || isIdentityCard) {
    documentTips.push(t('identityVerification.tips.document4'));
  }

  const selfieTips = [
    t('identityVerification.tips.selfie1'),
    t('identityVerification.tips.selfie2'),
    t('identityVerification.tips.selfie3'),
    t('identityVerification.tips.selfie4')
  ];

  // Doğrulama formu (ilk kez veya reddedilmiş durumdan sonra)
  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom align="center">
        {t('identityVerification.title')}
      </Typography>

      <Typography paragraph>
        {t('identityVerification.description')}
      </Typography>

      {rejectionReason && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('identityVerification.previousRejection')} {t('identityVerification.rejected.reason')}: {rejectionReason}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('identityVerification.firstName')}
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('identityVerification.lastName')}
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t('identityVerification.dateOfBirth')}
              value={formData.dateOfBirth}
              onChange={handleDateChange}
              renderInput={(params) => <TextField {...params} fullWidth required />}
              maxDate={new Date()}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('identityVerification.nationality')}
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>{t('identityVerification.documentType')}</InputLabel>
              <Select
                name="documentType"
                value={formData.documentType}
                onChange={handleChange}
                label={t('identityVerification.documentType')}
              >
                <MenuItem value="0">{t('identityVerification.documentTypes.identityCard')}</MenuItem>
                <MenuItem value="1">{t('identityVerification.documentTypes.passport')}</MenuItem>
                <MenuItem value="2">{t('identityVerification.documentTypes.driverLicense')}</MenuItem>
                <MenuItem value="3">{t('identityVerification.documentTypes.residencePermit')}</MenuItem>
                <MenuItem value="4">{t('identityVerification.documentTypes.foreignID')}</MenuItem>
                <MenuItem value="5">{t('identityVerification.documentTypes.blueCard')}</MenuItem>
                <MenuItem value="6">{t('identityVerification.documentTypes.militaryID')}</MenuItem>
                <MenuItem value="7">{t('identityVerification.documentTypes.studentID')}</MenuItem>
                <MenuItem value="8">{t('identityVerification.documentTypes.workPermit')}</MenuItem>
                <MenuItem value="9">{t('identityVerification.documentTypes.temporaryID')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              {t('identityVerification.uploadDocuments')}
            </Typography>
            <TipsBox title={t('identityVerification.tips.documentTitle')} items={documentTips} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('identityVerification.tips.formats')}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              color={fieldErrors.documentFrontImage ? 'error' : 'primary'}
              sx={{ height: 56, mb: 1 }}
            >
              {t('identityVerification.frontSide')}
              <input
                type="file"
                name="documentFrontImage"
                accept="image/jpeg,image/png"
                hidden
                onChange={handleFileChange}
                required
              />
            </Button>
            {fieldErrors.documentFrontImage && (
              <FormHelperText error>{fieldErrors.documentFrontImage}</FormHelperText>
            )}
            {preview.front && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <img
                  src={preview.front}
                  alt="ID Front"
                  style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
                />
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              color={fieldErrors.documentBackImage ? 'error' : 'primary'}
              sx={{ height: 56, mb: 1 }}
            >
              {isIdentityCard ? t('identityVerification.backSideRequired') : t('identityVerification.backSide')}
              <input
                type="file"
                name="documentBackImage"
                accept="image/jpeg,image/png"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {fieldErrors.documentBackImage ? (
              <FormHelperText error>{fieldErrors.documentBackImage}</FormHelperText>
            ) : isIdentityCard ? (
              <FormHelperText>{t('identityVerification.backSideMrzHint')}</FormHelperText>
            ) : null}
            {preview.back && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <img
                  src={preview.back}
                  alt="ID Back"
                  style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
                />
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              {t('identityVerification.selfie')}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TipsBox title={t('identityVerification.tips.selfieTitle')} items={selfieTips} />
            </Box>

            {cameraActive ? (
              <Box sx={{ position: 'relative', mb: 2 }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', borderRadius: 8 }}
                />
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 1,
                  gap: 2
                }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={takeSelfie}
                  >
                    {t('identityVerification.takeSelfie')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={stopCamera}
                  >
                    {t('identityVerification.cancelCamera')}
                  </Button>
                </Box>
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                {preview.selfie ? (
                  <Box sx={{ position: 'relative', width: '100%', textAlign: 'center' }}>
                    <img
                      src={preview.selfie}
                      alt="Selfie"
                      style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8 }}
                    />
                    <IconButton
                      color="error"
                      sx={{ position: 'absolute', top: 0, right: 0 }}
                      onClick={() => setImage('selfieImage', null)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2, width: '100%', flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<PhotoCamera />}
                      onClick={startCamera}
                    >
                      {t('identityVerification.openCamera')}
                    </Button>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      color={fieldErrors.selfieImage ? 'error' : 'primary'}
                    >
                      {t('identityVerification.uploadSelfie')}
                      <input
                        type="file"
                        name="selfieImage"
                        accept="image/jpeg,image/png"
                        hidden
                        onChange={handleFileChange}
                      />
                    </Button>
                  </Box>
                )}
                {fieldErrors.selfieImage && (
                  <FormHelperText error sx={{ alignSelf: 'flex-start' }}>{fieldErrors.selfieImage}</FormHelperText>
                )}
              </Box>
            )}
          </Grid>

          {/* KVKK m.6: biyometrik veri için ayrı, isteğe bağlı ve varsayılan olarak işaretsiz açık rıza */}
          <Grid item xs={12}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mr: 0 }}
              control={
                <Checkbox
                  checked={biometricConsent}
                  onChange={(e) => setBiometricConsent(e.target.checked)}
                  name="biometricConsent"
                  sx={{ mt: -0.75 }}
                />
              }
              label={
                <Typography variant="body2">
                  <LegalInline text={t('identityVerification.consent.label')} linkTarget="_blank" />
                </Typography>
              }
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
              {t('identityVerification.consent.optional')}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <LegalInline text={t('identityVerification.infoNotice')} linkTarget="_blank" />
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                t('identityVerification.submit')
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={cameraError}
        autoHideDuration={6000}
        onClose={() => setCameraError(false)}
        message={t('identityVerification.cameraError')}
      />
    </Paper>
  );
};

export default IdentityVerification;
